import { parseISO } from "date-fns";
import i18n, { getLocale } from "../../i18n";
import { formatDateWithoutHour } from "../date/formatedDate";

export interface CapacityMonth {
  key: string;
  year: number;
  month: number;
  data?: IBalanceCapacity;
}

export function buildCapacitySeries(data: IBalanceCapacity[], from: Date, to: Date): CapacityMonth[] {
  const byMonth = new Map(data.map(item => [`${item.year}-${item.month}`, item]));
  const result: CapacityMonth[] = [];
  const end = to.getFullYear() * 12 + to.getMonth();
  for (let index = from.getFullYear() * 12 + from.getMonth(); index <= end; index++) {
    const year = Math.floor(index / 12);
    const month = index % 12 + 1;
    const key = `${year}-${month}`;
    result.push({ key, year, month, data: byMonth.get(key) });
  }
  return result;
}

export function capacityMonthLabel(point: CapacityMonth) {
  return new Intl.DateTimeFormat(getLocale(), { month: "short", year: "numeric" })
    .format(new Date(point.year, point.month - 1, 1));
}

export function capacityValue(value: number | null | undefined) {
  if (value === undefined) return i18n.t<string>("reports.capacity.noData");
  if (value === null) return i18n.t<string>("reports.capacity.unavailable");
  return new Intl.NumberFormat(getLocale(), { maximumFractionDigits: 2 }).format(value);
}

export function capacityPeriod(data: IBalanceCapacity) {
  // These fields are calendar dates, not UTC instants.
  return i18n.t<string>("reports.capacity.periodRange", {
    start: formatDateWithoutHour(parseISO(data.period_start)),
    end: formatDateWithoutHour(parseISO(data.period_end)),
  });
}

export function capacitySeason(season: IBalanceCapacity["season"]) {
  return i18n.t<string>(`reports.capacity.${season === "DRY" ? "dry" : "rainy"}`);
}

export function capacityRows(points: CapacityMonth[], forExport = false): (string | number)[][] {
  const missing = forExport ? "" : i18n.t<string>("reports.capacity.noData");
  const value = (v: number | null | undefined) => forExport
    ? v === undefined ? "" : v === null ? i18n.t<string>("reports.capacity.unavailable") : v
    : capacityValue(v);
  return [
    [i18n.t<string>("reports.averageAnimals"), ...points.map(p => value(p.data?.average_animals))],
    [i18n.t<string>("reports.capacity.averageRate"), ...points.map(p => value(p.data?.average_capacity_rate))],
    [i18n.t<string>("reports.capacity.period"), ...points.map(p => p.data ? capacityPeriod(p.data) : missing)],
    [i18n.t<string>("reports.capacity.days"), ...points.map(p => p.data ? (forExport ? p.data.days_considered : capacityValue(p.data.days_considered)) : missing)],
    [i18n.t<string>("reports.capacity.season"), ...points.map(p => p.data ? capacitySeason(p.data.season) : missing)],
  ];
}
